import jwtDecode, { JwtPayload } from 'jwt-decode';
import { gql, GraphQLRequest, ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { SecureStorage } from '@pletly/common';
import { Feature } from '@pletly/shared';
import { eventEmitter } from '../services/eventEmitter';
import { User } from '../context/Auth';
import { client, restartWebSocketClient } from '../api';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const MEDIA_TOKEN_KEY = 'media_token';

function getToken() {
	return SecureStorage.get(TOKEN_KEY);
}

function getRefreshToken() {
	return SecureStorage.get(REFRESH_TOKEN_KEY);
}

function getMediaToken() {
	return SecureStorage.get(MEDIA_TOKEN_KEY);
}

function saveToken(token: string) {
	return SecureStorage.set(TOKEN_KEY, token);
}

function saveRefreshToken(token: string) {
	return SecureStorage.set(REFRESH_TOKEN_KEY, token);
}

function saveMediaToken(token: string) {
	return SecureStorage.set(MEDIA_TOKEN_KEY, token);
}

function deleteToken() {
	return Promise.all([
		SecureStorage.deleteItem(TOKEN_KEY),
		SecureStorage.deleteItem(REFRESH_TOKEN_KEY),
		SecureStorage.deleteItem(MEDIA_TOKEN_KEY),
	]);
}

function validateToken(token?: string | null) {
	if (!token) {
		return { valid: false, needRefresh: false };
	}

	const decoded = jwtDecode<JwtPayload>(token);
	if (!decoded || (decoded.exp && Date.now() >= decoded.exp * 1000)) {
		return { valid: false, needRefresh: true };
	}

	return { valid: true, needRefresh: false };
}

const RENEW_TOKEN = gql`
	mutation RefreshToken($refreshToken: String!) {
		refreshToken(data: { refreshToken: $refreshToken }) {
			accessToken
			refreshToken
			mediaToken
		}
	}
`;

interface RefreshToken {
	refreshToken: string;
	accessToken: string;
	mediaToken: string;
}

const renewToken = async (client: ApolloClient<NormalizedCacheObject | object>) => {
	const token = await getRefreshToken();

	const { needRefresh } = validateToken(token);

	if (needRefresh) {
		eventEmitter.emit('logout');

		return;
	}

	const { data } = await client.mutate<{ refreshToken: RefreshToken }>({
		variables: { refreshToken: token },
		mutation: RENEW_TOKEN,
	});
	const { refreshToken, accessToken, mediaToken } = data?.refreshToken as RefreshToken;
	await saveRefreshToken(refreshToken);
	await saveToken(accessToken);
	await saveMediaToken(mediaToken);
	eventEmitter.emit('tokenRefreshed');

	restartWebSocketClient();

	return { refreshToken, accessToken, mediaToken };
};

async function getAccessToken(
	client: ApolloClient<NormalizedCacheObject | object>,
	request?: GraphQLRequest,
): Promise<string | null> {
	const token = await getToken();

	if (
		request?.operationName &&
		['RefreshToken', 'ClearPushToken'].includes(request.operationName)
	) {
		return token;
	}

	const { needRefresh } = validateToken(token);

	if (!needRefresh) {
		return token;
	}

	const tokens = await renewToken(client);
	if (!tokens) {
		return null;
	}

	return tokens.accessToken;
}

function getUserFromToken(token: string) {
	return jwtDecode<
		JwtPayload & { userId: number; uuid: string; profile: Omit<User, 'id'>; features: Feature[] }
	>(token);
}

async function getUserId(operationName?: string) {
	const token = await getAccessToken(client, operationName ? { operationName } : undefined);
	if (!token) {
		return;
	}

	const { userId } = getUserFromToken(token);

	return userId.toString();
}

export {
	getAccessToken,
	getMediaToken,
	getRefreshToken,
	saveToken,
	saveRefreshToken,
	saveMediaToken,
	deleteToken,
	validateToken,
	renewToken,
	getUserFromToken,
	getUserId,
};
